import React, { useState, useMemo } from 'react';
import { GitCompare } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { useDashboard } from '../../DashboardContext';
import { S, computeHalvingMonths, SCENARIO_LABELS, annualizedROI, STATE_TAX_RATES, FEIE_EXCLUSION } from '../../lib/constants';
import { calculateStrategy, computePlanEconomics } from '../../lib/engine';

/* One plan, one configuration — so comparison is simply plan against plan.
   v1 plans held several strategies with one marked active; those are read via
   the same path their dashboard uses, so the numbers here match what the plan
   shows when opened. */
function planStrategy(plan) {
  const d = plan?.plan_data;
  if (!d) return null;
  if (d.strategy) return d.strategy;
  if (Array.isArray(d.strategies) && d.strategies.length) {
    const idx = Number.isInteger(d.activeStrategyIndex) ? d.activeStrategyIndex : 0;
    return d.strategies[idx] || d.strategies[0];
  }
  return null;
}

function runPlan(plan, blockHeight) {
  const d = plan?.plan_data;
  const strategy = planStrategy(plan);
  if (!strategy || !d?.market || strategy.isBenchmark) return null;
  const params = { ...d.market, halvingMonths: computeHalvingMonths(blockHeight) };
  try {
    const results = calculateStrategy(strategy, params);
    // Same function the dashboard uses, fed this plan's own tax settings, so
    // the multiple shown here matches what the plan shows when opened.
    const econ = computePlanEconomics(results, d.tax, {
      btcPrice: d.market.btcPrice,
      timeHorizon: d.market.timeHorizon,
      stateTaxRates: STATE_TAX_RATES,
      feieExclusion: FEIE_EXCLUSION,
    });
    return {
      strategy, results, econ,
      horizon: d.market.timeHorizon,
      scenario: d.market.priceScenario,
    };
  } catch { return null; }
}

const COLORS = ['#92700C', '#2563EB'];

export default function PlanCompareSection() {
  const { planMgr, marketDataMeta, fmt, fBTC } = useDashboard();
  const plans = planMgr?.plans || [];
  const advisorPlan = planMgr?.advisorPlan;
  const isClient = !!planMgr?.isClient;
  const [aId, setAId] = useState(null);
  const [bId, setBId] = useState(null);

  const heading = isClient ? 'Compare options' : 'Compare plans';

  // Defaults: the MinerOps plan vs the most recent option
  const planA = plans.find(p => p.id === aId) || advisorPlan || plans[0] || null;
  const planB = plans.find(p => p.id === bId) || plans.find(p => p.id !== planA?.id) || null;

  const blockHeight = marketDataMeta?.blockHeight;
  const runA = useMemo(() => runPlan(planA, blockHeight), [planA, blockHeight]);
  const runB = useMemo(() => runPlan(planB, blockHeight), [planB, blockHeight]);

  if (!planMgr?.online) {
    return (
      <div style={{ ...S.card }} className="p-8 text-center">
        <GitCompare className="w-8 h-8 mx-auto mb-3" style={{ color: '#D4D4D4' }} />
        <h3 className="font-semibold mb-1" style={{ color: '#1A1A1A', fontSize: 16 }}>Nothing to compare yet</h3>
        <p className="text-sm mx-auto" style={{ color: '#737373', maxWidth: 420 }}>
          Comparison works on saved plans, which sync through your MinerOps account.
          Sign in on a full deployment, save two, and they&apos;ll appear here side by side.
        </p>
      </div>
    );
  }
  if (plans.length < 2) {
    return (
      <div style={{ ...S.card }} className="p-6">
        <h3 className="font-semibold flex items-center gap-2 mb-2" style={{ color: '#1A1A1A' }}>
          <GitCompare className="w-4 h-4" style={{ color: S.C.gold }} /> {heading}
        </h3>
        <p className="text-sm" style={{ color: '#525252' }}>
          {isClient
            ? 'You need one option of your own to compare against your MinerOps plan. Open the plan, change anything you want to test, and save it as your own option. It will show up here.'
            : 'Save a second plan for this client from the Plan bar, then come back here.'}
        </p>
      </div>
    );
  }

  const selectStyle = { background: '#FFF', border: '1px solid #D4D4D4', borderRadius: 4, padding: '8px 12px', fontSize: 13, color: '#1A1A1A', cursor: 'pointer', minWidth: 200 };
  const planLabel = (p) => `${p.created_by_role === 'advisor' ? '★ ' : ''}${p.name}`;

  // Merge NAV series onto one axis (plans can have different horizons)
  const chartData = [];
  const maxLen = Math.max(runA?.results.monthlyData.length || 0, runB?.results.monthlyData.length || 0);
  for (let i = 0; i < maxLen; i++) {
    chartData.push({
      month: i + 1,
      a: runA?.results.monthlyData[i]?.nav ?? null,
      b: runB?.results.monthlyData[i]?.nav ?? null,
    });
  }
  const yearTicks = Array.from({ length: Math.floor(maxLen / 12) }, (_, i) => (i + 1) * 12);

  const metrics = [
    ['Final net worth (NAV)', r => fmt(r.results.finalNAV)],
    ['BTC accumulated', r => `${fBTC(r.results.finalTreasury, 2)} BTC`],
    ['Return on cash invested', r => {
      const roi = r.results.finalROI || 0;
      const a = annualizedROI(roi, r.horizon);
      return a !== null ? `${roi.toFixed(0)}% (≈ ${a.toFixed(0)}%/yr)` : `${roi.toFixed(0)}%`;
    }],
    ['Break-even (after write-offs)', r => {
      const m = r.econ?.postTaxBreakEvenMonth;
      return m ? `Month ${m}` : (r.econ?.taxApplied ? 'Not reached' : 'Tax analysis off');
    }],
    ['Break-even (before write-offs)', r => r.results.breakEvenMonth ? `Month ${r.results.breakEvenMonth}` : 'Not reached'],
    ['Payback (after write-offs)', r => {
      const m = r.econ?.postTaxPaybackMonth;
      return m ? `Month ${m}` : (r.econ?.taxApplied ? 'Not reached' : 'Tax analysis off');
    }],
    ['Cash at end', r => fmt(r.results.finalCash)],
    ['Every $1 in becomes', r => {
      const m = r.econ?.miningMultiple ?? r.results.miningMultiplier ?? 0;
      return `$${m.toFixed(2)} of BTC`;
    }],
    ['Before write-offs', r => `$${(r.results.miningMultiplier || 0).toFixed(2)} of BTC`],
    ['Cost per BTC (after tax)', r => r.econ ? fmt(r.econ.netCostPerBTC) : '—'],
    ['Write-offs claimed', r => r.econ?.taxApplied ? fmt(r.econ.taxSavings) : 'Tax analysis off'],
    ['Total miners', r => `${r.strategy.startingMiners + (r.strategy.expansions || []).reduce((s, e) => s + (e.miners || 0), 0)}`],
    ['Horizon', r => `${r.horizon} years`],
    ['Price assumption', r => SCENARIO_LABELS[r.scenario] || '—'],
  ];

  const winnerNAV = runA && runB ? (runA.results.finalNAV >= runB.results.finalNAV ? 0 : 1) : null;

  /* One side of the comparison: just the plan. */
  const side = (label, plan, setPlan, choices) => (
    <select
      value={plan?.id ?? ''}
      onChange={e => setPlan(Number(e.target.value))}
      style={selectStyle}
      aria-label={label}
    >
      {choices.map(p => <option key={p.id} value={p.id}>{planLabel(p)}</option>)}
    </select>
  );

  return (
    <div className="space-y-6 print-deliverable">
      <div style={{ ...S.card }} className="p-5 print-block">
        <h3 className="font-semibold flex items-center gap-2 mb-1" style={{ color: '#1A1A1A', fontSize: 16 }}>
          <GitCompare className="w-4 h-4" style={{ color: S.C.gold }} /> {heading}
        </h3>
        <p className="text-xs mb-4" style={{ color: '#737373' }}>
          Same math, side by side. ★ marks the plan MinerOps built.
        </p>

        <div className="flex items-start gap-3 flex-wrap mb-5 no-print">
          {side('Plan A', planA, setAId, plans)}
          <span style={{ color: '#737373', fontSize: 13, paddingTop: 9 }}>vs</span>
          {side('Plan B', planB, setBId, plans.filter(p => p.id !== planA?.id))}
        </div>

        {(!runA || !runB) ? (
          <div className="text-sm" style={{ color: '#737373' }}>
            {!runA && !runB ? 'Neither plan has a usable configuration.'
              : `${planLabel(!runA ? planA : planB)} has no usable configuration, so there is nothing to compare against.`}
          </div>
        ) : (
          <>
            {/* Verdict line */}
            <div className="p-4 mb-3" style={{ background: '#F5F5F5', border: '1px solid #D4D4D4', borderRadius: 8, borderLeft: `6px solid ${COLORS[winnerNAV]}` }}>
              <span className="text-sm" style={{ color: '#1A1A1A' }}>
                <strong>{planLabel(winnerNAV === 0 ? planA : planB)}</strong> comes out ahead by{' '}
                <strong style={S.tn(COLORS[winnerNAV])}>{fmt(Math.abs(runA.results.finalNAV - runB.results.finalNAV))}</strong> in projected net worth.
              </span>
            </div>

            {/* Metric table */}
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '8px 10px', borderBottom: '2px solid #D4D4D4', color: '#525252', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}></th>
                  {[planA, planB].map((p, col) => (
                    <th key={p.id} style={{ textAlign: 'right', padding: '8px 10px', borderBottom: '2px solid #D4D4D4', color: COLORS[col], fontSize: 12 }}>
                      {planLabel(p)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {metrics.map(([label, get], i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #F5F5F5' }}>
                    <td style={{ padding: '8px 10px', color: '#525252' }}>{label}</td>
                    <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600, ...S.tn('#1A1A1A') }}>{get(runA)}</td>
                    <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600, ...S.tn('#1A1A1A') }}>{get(runB)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>

      {runA && runB && (
        <div style={{ ...S.card }} className="p-5 print-block">
          <div style={S.labelSm}>Projected net worth over time</div>
          <ResponsiveContainer width="100%" height={320}>
            <LineChart data={chartData} margin={{ top: 12, right: 16, left: 8, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 6" stroke="#E5E5E5" />
              <XAxis dataKey="month" ticks={yearTicks} tickFormatter={m => `Yr ${m / 12}`} tick={{ fill: '#737373', fontSize: 11 }} />
              <YAxis tickFormatter={fmt} tick={{ fill: '#737373', fontSize: 11 }} width={70} />
              <Tooltip contentStyle={S.ttip} formatter={(v, n) => [fmt(v), n]} labelFormatter={m => `Month ${m}`} />
              <Legend />
              <Line type="monotone" dataKey="a" name={planLabel(planA)} stroke={COLORS[0]} strokeWidth={2.5} dot={false} connectNulls={false} />
              <Line type="monotone" dataKey="b" name={planLabel(planB)} stroke={COLORS[1]} strokeWidth={2.5} dot={false} connectNulls={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
